import React, { useEffect, useState } from "react";
import {
  ReportBespokeStoreContextProvider,
  useGlobalCurrency,
  usePortfolio,
  usePreferences,
  useReportBespoke,
} from "@iliotech/data-wire";
import { PDFExport } from "@progress/kendo-react-pdf";
import { useParams } from "react-router";
import { ReportLayoutManager } from "./components/ReportLayoutManager/ReportLayoutManager";
import s from "./ReportGenV2.module.scss";
import { ReportPreview } from "./components/ReportPreview/ReportPreview";
import { ReportDownloadButton } from "./components/ReportDownloadButton/ReportDownloadButton";
import { ReportFilters } from "./components/ReportFilters/ReportFilters";
import classNames from "classnames";
import moment from "moment";
import { Loader, useNotification } from "src/main";
import { useReportPeriod } from "./hooks/useReportPeriod";
import { ReportSectionWrapper } from "./wrappers/ReportSectionWrapper";
import { DndContext, DragOverlay, DragStartEvent } from "@dnd-kit/core";
import { useDragHandlers } from "./hooks/useDragHandlers";
import { MdAddBox } from "react-icons/md";
import { observer } from "mobx-react-lite";
import { ReportZoomControl } from "./components/ReportZoomControl/ReportZoomControl";
import { ReportSaveTemplateButton } from "./components/ReportSaveTemplateButton/ReportSaveTemplateButton";
import { ReportLayoutButton } from "./components/ReportLayoutManager/components/ReportLayoutButton";
import { findReportLayoutItem } from "./components/ReportLayoutManager/reportLayoutSections";
import { ReportPreviewButton } from "./components/ReportPreviewButton/ReportPreviewButton";

export const ReportGenV2Page = () => {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const store = useReportBespoke();
  const { loaded } = useReportPeriod(portfolioId!);
  const { handleDragOver, handleDragEnd } = useDragHandlers();
  const [activeId, setActiveId] = useState<string | null>(null);
  const item = findReportLayoutItem(activeId!);
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  };
  if (!loaded) return <Loader />;
  return (
    <div className={s.container}>
      <ReportBespokeStoreContextProvider value={store}>
        <DndContext
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <ReportGenV2PageInner />
          <DragOverlay>
            {item ? (
              <ReportLayoutButton
                icon={item?.icon!}
                id={item?.id!}
                text={item?.text!}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </ReportBespokeStoreContextProvider>
    </div>
  );
};
const ReportGenV2PageInner = observer(() => {
  const { success, error } = useNotification();
  const portfolioId = useParams<{ portfolioId: string }>().portfolioId;
  const store = useReportBespoke();
  const { setBaseCurrency, setCurrency } = useGlobalCurrency();
  const portfolioRequest = usePortfolio(portfolioId!);
  const portfolioInfo = portfolioRequest.data?.data;
  const landscape = store.isLandscape();
  const [scale, setScale] = useState(1);
  const { updateMiscPreference, preferences } = usePreferences();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (preferences?.misc?.reportTemplate) {
      store.loadReport(preferences?.misc?.reportTemplate);
    }
  }, [preferences?.misc?.reportTemplate]);

  useEffect(() => {
    setBaseCurrency(portfolioInfo?.currencySymbol || "");
    setCurrency(portfolioInfo?.currencySymbol || "");
  }, [portfolioInfo]);

  const onSaveTemplate = async () => {
    setIsSaving(true);
    try {
      const data = await updateMiscPreference(
        "reportTemplate",
        store.saveReport()
      );
      setIsSaving(false);
      success({
        title: "Template saved",
        description: "Template saved to your account",
      });
    } catch (err) {
      error({
        title: "Template save failed",
        description: "Template save failed",
      });
      setIsSaving(false);
    }
  };
  const pdfExportComponent = React.useRef<PDFExport>(null);

  const previewPDF = () => {
    store.setGenerating(!store.generating);
  };
  const exportPDFWithComponent = () => {
    store.setGenerating(true);
    setTimeout(() => {
      if (pdfExportComponent.current) {
        pdfExportComponent.current.save(() => {
          store.setGenerating(false);
        });
      }
    }, 500);
  };

  return (
    <div className={classNames(s.container, store.generating && s.generating)}>
      <ReportLayoutManager />
      <div className={s.reportPreviewWrapper}>
        <ReportFilters
          riskAssetView={portfolioInfo?.riskAssetView!}
          inceptionDate={portfolioInfo?.inceptionDate!}
        />
        <ReportPreview
          isLandscape={landscape}
          scale={scale}
          onExport={exportPDFWithComponent}
        >
          <div className={s.reportContent}>
            <PDFExport
              paperSize={"A4"}
              landscape={landscape}
              scale={0.7}
              forcePageBreak=".page-break"
              keepTogether=".keep-together"
              ref={pdfExportComponent}
              fileName={`Report - ${moment().format("DD-MM-YYYY")}`}
              author="Portfolio Analytics Team"
            >
              {/* Cover Page */}
              {Object.values(store.pages).map((page, index) => (
                <ReportSectionWrapper
                  portfolioName={portfolioInfo?.name!}
                  key={page.id}
                  pageId={page.id}
                  isLandscape={landscape}
                />
              ))}
            </PDFExport>
            <div
              onClick={() => store.addPage("$portfolioName")}
              className={s.addPage}
            >
              <MdAddBox size={80} />
            </div>
          </div>
        </ReportPreview>
        <ReportSaveTemplateButton
          loading={isSaving}
          onExport={() => onSaveTemplate()}
        />
        <ReportDownloadButton onExport={exportPDFWithComponent} />
        <ReportPreviewButton onExport={previewPDF} />
        <ReportZoomControl scale={scale} setScale={setScale} />
      </div>
    </div>
  );
});
